import React, { useEffect, useState } from "react";
import axios from "axios";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/react-splide/css";

import {
  PROMOTIONS_API_URL,
  PROMOTIONS_API_KEY,
} from "../config";

import "./styles/Promotions.css";

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [timerKey, setTimerKey] = useState(0);
  const [loading, setLoading] = useState(true);

  // =========================================================
  // FETCH PROMOTIONS
  // =========================================================

  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        setLoading(true);

        const response = await axios.get(
          PROMOTIONS_API_URL,
          {
            headers: {
              "x-api-key": PROMOTIONS_API_KEY,
            },
            timeout: 10000,
          }
        );

        if (!response.data?.success) {
          throw new Error("Failed to load promotions");
        }

        const data = Array.isArray(response.data.data)
          ? response.data.data
          : [];

        // Only show Tubbees promotions
        const tubbeesPromotions = data.filter(
          (promotion) =>
            promotion.type?.name?.toLowerCase() === "tubbees"
        );

        // Latest start date first
        const sortedPromotions = [...tubbeesPromotions].sort(
          (a, b) => {
            const dateA = new Date(a.start_date).getTime();
            const dateB = new Date(b.start_date).getTime();

            return dateB - dateA;
          }
        );

        setPromotions(sortedPromotions);
        setActiveIndex(0);
      } catch (error) {
        console.error("Failed to load promotions:", error);

        // API failed → show empty state
        setPromotions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPromotions();
  }, []);

  // =========================================================
  // AUTO ROTATION
  // =========================================================

  useEffect(() => {
    if (promotions.length <= 1) return;

    const interval = setInterval(() => {
      setActiveIndex((current) =>
        current === promotions.length - 1
          ? 0
          : current + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [promotions.length, timerKey]);

  // =========================================================
  // CHANGE PROMOTION
  // =========================================================

  const changePromotion = (index) => {
    setActiveIndex(index);
    setTimerKey((current) => current + 1);
  };

  // =========================================================
  // PREVIOUS
  // =========================================================

  const previousPromotion = () => {
    setActiveIndex((current) =>
      current === 0
        ? promotions.length - 1
        : current - 1
    );

    setTimerKey((current) => current + 1);
  };

  // =========================================================
  // NEXT
  // =========================================================

  const nextPromotion = () => {
    setActiveIndex((current) =>
      current === promotions.length - 1
        ? 0
        : current + 1
    );

    setTimerKey((current) => current + 1);
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <section className="promotions">
        <div className="promotions-loading">
          <div className="promotions-loader" />
        </div>
      </section>
    );
  }

  // =========================================================
  // NO PROMOTIONS / API ERROR
  // =========================================================

  if (!promotions.length) {
    return (
      <section className="promotions promotions-empty">
        <div className="promotions-empty-content">
          <span className="copy-small">
            TUBBEES PROMOTIONS
          </span>

          <h2>
            No promos
            <br />
            <span>available.</span>
          </h2>

          <p>
            Check back soon for something good!
          </p>
        </div>
      </section>
    );
  }

  // =========================================================
  // MAIN PROMOTIONS
  // =========================================================

  return (
    <section className="promotions">

      <div className="promotions-bg">
        {promotions.map((promotion, index) => (
          <img
            key={promotion.id || index}
            src={promotion.image}
            alt=""
            className={`promotions-bg-image ${
              index === activeIndex ? "is-active" : ""
            }`}
          />
        ))}
      </div>

      <div className="promotions-container">

        {/* <div className="promotions-topbar">

          <div className="promotions-brand">
            <span className="brand-dot" />
            <span>TUBBEES</span>
          </div>

          <div className="promotions-top-label">
            <span>WHAT'S ON</span>
            <span className="top-label-line" />
            <span>2026</span>
          </div>

        </div> */}

        <div className="promotions-main">

          <div className="promotions-copy">

            <div className="copy-number">
              <span>
                {String(activeIndex + 1).padStart(2, "0")}
              </span>

              <div className="copy-number-line" />

              <span>
                {String(promotions.length).padStart(2, "0")}
              </span>
            </div>

            <div className="copy-content">

              <span className="copy-small">
                FRESH DEALS
              </span>

              <h2>
                Something
                <br />
                <span>good?</span>
              </h2>

              <p>
                Check out what's on at Tubbees right now.
              </p>

            </div>

            <div className="promotions-controls">

              <button
                type="button"
                onClick={previousPromotion}
                aria-label="Previous promotion"
                className="control-button"
              >
                <span>←</span>
              </button>

              <button
                type="button"
                onClick={nextPromotion}
                aria-label="Next promotion"
                className="control-button control-button-dark"
              >
                <span>→</span>
              </button>

            </div>

          </div>

          <div className="promotions-visual">

            <div className="visual-circle" />

            <div className="visual-circle-small" />

            <div className="promotion-artwork">
              {promotions.map((promotion, index) => (
                <img
                  key={promotion.id || index}
                  src={promotion.image}
                  alt={
                    promotion.name || "Tubbees promotion"
                  }
                  className={`promotion-artwork-image ${
                    index === activeIndex ? "is-active" : ""
                  }`}
                />
              ))}
            </div>

            <div className="visual-label">
              <span>LIMITED</span>
              <span>OFFER</span>
            </div>

            <div className="visual-index">
              {String(activeIndex + 1).padStart(2, "0")}
            </div>

          </div>

        </div>

        {promotions.length > 1 && (
          <div className="promotion-rail">

            <div className="rail-heading">
              <span>ALL OFFERS</span>
            </div>

            <div className="rail-items">

              <Splide
                options={{
                  type: "slide",
                  perPage: 3,
                  perMove: 1,
                  gap: "10px",
                  arrows: false,
                  pagination: false,
                  drag: true,
                  snap: false,
                  autoWidth: true,
                  rewind: false,
                  breakpoints: {
                    768: {
                      perPage: 1,
                      autoWidth: true,
                    },
                  },
                }}
                aria-label="Tubbees offers"
              >

                {promotions.map((promotion, index) => (
                  <SplideSlide
                    key={promotion.id || index}
                  >
                    <button
                      type="button"
                      className={`rail-item ${
                        index === activeIndex
                          ? "is-active"
                          : ""
                      }`}
                      onClick={() =>
                        changePromotion(index)
                      }
                    >

                      <div className="rail-image">
                        <img
                          src={promotion.image}
                          alt=""
                        />
                      </div>

                      <div className="rail-info">

                        <span className="rail-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span className="rail-name">
                          {promotion.name || "Tubbees offer"}
                        </span>

                      </div>

                      <span className="rail-arrow">
                        →
                      </span>

                    </button>
                  </SplideSlide>
                ))}

              </Splide>

            </div>

          </div>
        )}

        <div className="promotions-footer">
          <span>TUBBEES</span>
          <span className="footer-star">✦</span>
          <span>GOOD STUFF</span>
          <span className="footer-star">✦</span>
          <span>GREAT DEALS</span>
        </div>

      </div>

    </section>
  );
};

export default Promotions;